import React from 'react';

import Link from '../../../../library/link/link';
import MinimumPriceMessage from '../../../../components/minimum-price-message/minimum-price-message';
import ProductImage from '../../../../components/product-image/product-image';
import WebOnlyFlag from '../../../../components/web-only-flag/web-only-flag';
import ProductListPackageCardHeader from './components/product-list-package-card-header/product-list-package-card-header';
import { WhiteAngleRegularIcon } from '../../../../shared/icons/icons';
import ProductListCardActions from '../product-list-card-actions/product-list-card-actions';
import ProductListCardDetails from '../product-list-card-details/product-list-card-details';
import ProductListCardHighlight from '../product-list-card-highlight/product-list-card-highlight';
import ProductListCardPrice from '../product-list-card-price/product-list-card-price';

import {
  useOptimisticData,
  useProductQuantity
} from '../../../../shared/hooks/hooks';

import {
  getShouldShowMinimumPriceMessage,
  isWebOnlyProduct
} from '../../../../shared/utils/product/product';

import { BundleType } from '../../../../shared/types/graphql-types';
import { BundleData, ProductItem } from '../../../../shared/types/product';

import './product-list-package-card.scss';

interface ProductListPackageCardProps {
  onAddProductClick: (productItem: ProductItem) => void;
  onCheckInventoryClick: () => void;
  onViewProductClick: () => void;
  product: BundleData;
}

function ProductListPackageCard(props: ProductListPackageCardProps) {
  const {
    onAddProductClick,
    onCheckInventoryClick,
    onViewProductClick,
    product: prebuiltPackage
  } = props;

  const { product1, product2, url: productDetailUrl = '' } = prebuiltPackage;
  const { defaultQuantity, quantity, setQuantity, totalQuantity } =
    useProductQuantity({
      bundleType: BundleType.TireWheelPackage
    });

  const optimisticData = useOptimisticData();

  const showMinimumPriceMessage = getShouldShowMinimumPriceMessage(product1);

  const isWebOnly = isWebOnlyProduct(product1);

  if (!product1 || !product2) return null;

  if (window['isPreRendering']) {
    return <div styleName="container" />;
  }

  function setOptimisticProductData() {
    optimisticData.write({ prebuiltPackage });
    onViewProductClick?.();
  }

  return (
    <div
      data-testid="product-list-package-card"
      id={product1 && product1.code ? `product-code-${product1.code}` : ''}
      styleName="container"
    >
      <div styleName="img-col">
        <ProductListCardHighlight
          hideTreadwellRank
          hideWebOnly
          product={product1}
        />

        <Link
          onClick={setOptimisticProductData}
          styleName="images"
          to={productDetailUrl}
        >
          <ProductImage
            height={190}
            product={product1}
            styleName="tire-img"
            width={190}
          />
          <ProductImage
            height={190}
            product={product2}
            styleName="wheel-img"
            width={190}
          />
        </Link>

        {isWebOnly && (
          <WebOnlyFlag className="product-list-package-card__web-only-flag" />
        )}

        <WhiteAngleRegularIcon styleName="icon" />
      </div>

      <ProductListPackageCardHeader
        prebuiltPackage={prebuiltPackage}
        quantity={quantity}
      />

      <ProductListCardDetails
        header="Tire:"
        hideAvailability
        hideCompare
        hideImage
        onCheckInventoryClick={onCheckInventoryClick}
        onViewProductClick={onViewProductClick}
        product={product1}
        productDetailUrl={productDetailUrl}
        quantity={quantity}
      />

      <div styleName="product-one-price">
        <ProductListCardPrice
          hideFoundItLower
          hidePercentageSaved
          product={product1}
          quantity={quantity}
          showStaticQuantity={Boolean(prebuiltPackage)}
          totalQuantity={totalQuantity}
        />
        {showMinimumPriceMessage && (
          <MinimumPriceMessage message={product1?.mapDisplayRule?.message} />
        )}
      </div>

      <hr styleName="package-divider" />

      <div styleName="wheel-details">
        <ProductListCardDetails
          header="Wheel:"
          hideAvailability
          hideImage
          hideViewOnVehicleAddToCart
          hideViewOnVehicleFilters
          onCheckInventoryClick={onCheckInventoryClick}
          onViewProductClick={onViewProductClick}
          product={product2}
          productDetailUrl={productDetailUrl}
          quantity={quantity}
        />
      </div>

      <ProductListCardActions
        defaultQuantity={defaultQuantity}
        hideFoundItLower
        onAddProductClick={onAddProductClick}
        onQuantityChange={setQuantity}
        prebuiltPackage={prebuiltPackage}
        product={product2}
        quantity={quantity}
        totalQuantity={totalQuantity}
      />
    </div>
  );
}

export default ProductListPackageCard;
